import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { authErrorMessage, networkErrorMessage, socialErrorMessage } from '@/lib/auth-error';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { authClient } from '../lib/auth-client';
import { useLandAfterAuth } from '@/lib/landing';

/**
 * How long to wait for the session to materialise after a successful sign-in
 * before calling it a failure. Generous: this only fires when something is
 * genuinely wrong, and a false alarm on a slow connection is its own bug.
 */
const SESSION_WATCHDOG_MS = 8000;

export default function LoginPage() {
  const { data: session } = authClient.useSession();
  const [searchParams] = useSearchParams();
  const justReset = searchParams.get('reset') === '1';
  const justVerified = searchParams.get('verified') === '1';
  // A social sign-in that failed redirects back here with the reason attached.
  const socialError = socialErrorMessage(searchParams.get('error'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);

  const watchdog = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearWatchdog = () => {
    if (watchdog.current) {
      clearTimeout(watchdog.current);
      watchdog.current = null;
    }
  };

  useEffect(() => clearWatchdog, []);

  useLandAfterAuth(session, clearWatchdog);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setResent(false);
    setLoading(true);

    try {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        const code = result.error.code;
        if (code === 'EMAIL_NOT_VERIFIED') {
          setUnverified(true);
        } else {
          setError(authErrorMessage(result, 'Sign in failed'));
        }
        setLoading(false);
        return;
      }

      // On success, the session atom updates asynchronously; the effect above
      // handles navigation once ProtectedRoute will see a valid session.
      //
      // But a 200 means the password was right, not that the session survived.
      // If the browser refuses the cookie — blocked for the site, or shadowed
      // by a same-named cookie from another app on the parent domain — the
      // atom never turns truthy and this screen spins forever with nothing on
      // it. That is the least diagnosable state the app can be in, so put a
      // deadline on it and say what went wrong.
      clearWatchdog();
      watchdog.current = setTimeout(() => {
        setLoading(false);
        setError(
          'Signed in, but the session did not stick — your browser is most likely ' +
            'blocking cookies for this site. Try a private window, or allow cookies ' +
            'for this address and sign in again.',
        );
      }, SESSION_WATCHDOG_MS);
    } catch {
      setError(networkErrorMessage());
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    try {
      await authClient.sendVerificationEmail({
        email,
        callbackURL: `${window.location.origin}/verify-email?status=success`,
      });
      setResent(true);
    } catch {
      setError('Could not resend verification email');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <h1 className="display-tight text-3xl font-semibold">Welcome back</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to continue planning your day
          </p>
        </div>

        <div className="rounded-3xl bg-card p-8 shadow-ambient">
          <form onSubmit={handleSubmit} className="space-y-5">
            {(justReset || justVerified) && (
              <div className="rounded-2xl bg-[var(--color-primary-container)] p-3 text-sm text-[var(--color-accent-foreground)]">
                {justReset
                  ? 'Password updated. Sign in with your new password.'
                  : 'Email verified. You can sign in now.'}
              </div>
            )}
            {(error || socialError) && (
              <div className="rounded-2xl bg-destructive/10 p-3 text-sm text-destructive">
                {error || socialError}
              </div>
            )}
            {unverified && (
              <div className="space-y-2 rounded-2xl bg-[var(--color-tertiary-container)] p-3 text-sm text-foreground">
                <p>Please verify your email before signing in.</p>
                {resent ? (
                  <p className="text-xs">Verification email resent. Check your inbox.</p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    className="text-xs font-medium underline underline-offset-2"
                  >
                    Resend verification email
                  </button>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="label-caps text-xs text-muted-foreground">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus-visible:bg-[var(--color-surface-bright)] focus-visible:shadow-(--shadow-input-focus) focus-visible:border-[var(--color-focus-ring)] focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:outline-none"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="label-caps text-xs text-muted-foreground">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                >
                  Forgot?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus-visible:bg-[var(--color-surface-bright)] focus-visible:shadow-(--shadow-input-focus) focus-visible:border-[var(--color-focus-ring)] focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary-gradient inline-flex h-11 w-full items-center justify-center rounded-full px-6 text-sm font-medium text-white shadow-ambient transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="flex items-center gap-3 pt-1">
              <span className="h-px flex-1 bg-[var(--color-outline-variant)]" />
              <span className="text-xs text-muted-foreground">or</span>
              <span className="h-px flex-1 bg-[var(--color-outline-variant)]" />
            </div>

            <GoogleSignInButton label="Continue with Google" onError={setError} />
          </form>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/signup" className="font-medium text-secondary underline-offset-4 hover:underline">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
